<template>
  <v-app>
    <v-system-bar heigth="32" app color="secondary" class="sync-status-bar">
      <!-- Left side: Cache count (always shows available data) -->
      <v-icon small>mdi-database</v-icon>
      <span v-if="cacheCurrentSize > 0" class="text-overline ml-1">
        {{ cacheCurrentSize }}
      </span>
      <v-spacer></v-spacer>
      <!-- Right side: Sync status with progress -->
      <template v-if="isLoading">
        <v-progress-circular
          indeterminate
          size="14"
          width="2"
          color="primary"
          class="mr-1"
        ></v-progress-circular>
        <span class="text-overline">
          <template v-if="syncProgress.total > 0">
            {{ syncProgressPercent }}%
          </template>
          <template v-else-if="syncProgress.loaded > 0">
            Syncing... {{ syncProgress.loaded }}
          </template>
          <template v-else>Syncing...</template>
        </span>
      </template>
      <template v-else-if="isSynced">
        <v-icon small class="mr-1">mdi-check-circle</v-icon>
        <span class="text-overline">Synced</span>
      </template>
      <!-- The next-generation cache (engine: 'next'), read-only. -->
      <span
        v-if="$wob.engine === 'next'"
        class="text-overline ml-2"
        :title="$wob.syncError || ''"
      >
        next{{ $wob.readOnly ? ' · read-only' : ''
        }}{{ $wob.client.isAuthenticated && !$wob.isSynced ? ' · syncing' : ''
        }}{{ $wob.isSynced && $wob.syncError ? ' · sync failed' : '' }}
      </span>
    </v-system-bar>

    <v-navigation-drawer
      app
      disable-resize-watcher
      v-model="drawer"
      right
      clipped
    >
      <!-- TODO separate the login to separate sign-in/profile entries, not in menu array -->
      <v-list dense nav>
        <v-list-item v-for="item in menu" :key="item.title" link :to="item.to">
          <v-list-item-icon>
            <v-icon>{{ item.icon }}</v-icon>
          </v-list-item-icon>
          <v-list-item-content>
            <v-list-item-title>
              {{ item.title }}
            </v-list-item-title></v-list-item-content
          >
        </v-list-item>
      </v-list>
    </v-navigation-drawer>

    <v-app-bar app dark clipped-right color="primary">
      <!-- div used to remove truncation of title text, unclear why it works -->
      <!-- display: block -->
      <v-img
        contain
        max-height="48px"
        max-width="72px"
        src="wob-light.png"
      ></v-img>

      <v-app-bar-title>
        <!-- display: block -->
        <!-- WoB Explorer
        <v-icon dense right small color="secondary">mdi-flask-outline</v-icon>
        -->
      </v-app-bar-title>

      <v-spacer></v-spacer>

      <v-app-bar-nav-icon
        @click="drawer = !drawer"
        v-if="$vuetify.breakpoint.xs"
      ></v-app-bar-nav-icon>

      <template v-if="$vuetify.breakpoint.smAndUp">
        <v-btn text to="/search">
          <v-icon color="secondary" left>mdi-magnify</v-icon>Search
        </v-btn>

        <v-btn text @click="showBot = !showBot" to="">
          <!-- display: block -->
          <v-icon color="secondary" left>mdi-brain</v-icon>
          Bot
        </v-btn>

        <v-menu
          v-if="!$wob.readOnly"
          open-on-hover
          offset-y
          style="z-index: 20"
        >
          <template v-slot:activator="{ on, attrs }">
            <v-btn v-bind="attrs" v-on="on" text>
              <!-- display: block -->
              <v-icon color="secondary" left>mdi-database-edit-outline</v-icon>
              Manage
            </v-btn>
          </template>
          <v-list-item-group>
            <v-list nav dense>
              <v-list-item
                v-for="(item, index) in manageMenu"
                :key="index"
                :disabled="item.disable"
                @click.stop="item.handler"
              >
                <v-list-item-icon>
                  <v-icon v-text="item.icon" color="secondary"></v-icon>
                </v-list-item-icon>

                <!-- display: block -->
                <v-list-item-title class="text-body-1">
                  {{ item.title }}
                </v-list-item-title>
              </v-list-item>
            </v-list></v-list-item-group
          >
        </v-menu>

        <v-btn text to="/tree">
          <v-icon color="secondary" left>mdi-file-tree</v-icon>Trees
        </v-btn>

        <v-btn text to="/assets">
          <v-icon color="secondary" left>mdi-cube-outline</v-icon>Assets
        </v-btn>

        <v-btn text to="/map">
          <v-icon color="secondary" left>mdi-map-outline</v-icon>Map
        </v-btn>

        <!--TODO placeholder for bot -->
        <v-dialog v-model="showBot" max-width="400">
          <v-card>
            <v-toolbar color="lime darken-2" dark>
              <v-toolbar-title>Bot</v-toolbar-title>
              <v-spacer></v-spacer>
              <!-- display: block -->
              <v-btn icon @click="showBot = false">
                <v-icon>mdi-close</v-icon>
              </v-btn>
            </v-toolbar>
            <!-- display: block -->
            <v-card-text class="text-body-1 pt-5">
              This is a placeholder for the WoB AI assistant. It will complement
              the graph search with a free text search over any uploaded
              documents.
            </v-card-text>
          </v-card>
        </v-dialog>

        <v-menu
          bottom
          v-model="profileVisible"
          :offset-y="true"
          :close-on-content-click="false"
          min-width="320px"
          style="z-index: 20"
        >
          <template v-slot:activator="{ on, attrs }">
            <!-- display: block -->
            <v-btn text v-bind="attrs" v-on="on">
              <v-icon color="secondary" left>{{
                $wob.client.isAuthenticated ? 'mdi-cog' : 'mdi-login'
              }}</v-icon
              >{{ $wob.client.isAuthenticated ? 'Settings' : 'Sign In' }}
            </v-btn>
          </template>
          <!-- display: block -->
          <v-card>
            <v-toolbar color="lime darken-2" dark>
              <v-toolbar-title>Settings</v-toolbar-title>
              <v-spacer></v-spacer>
              <!-- display: block -->
              <v-btn icon @click="profileVisible = false">
                <v-icon>mdi-close</v-icon>
              </v-btn>
            </v-toolbar>
            <v-list flat>
              <!-- User Section -->
              <v-list-item>
                <v-list-item-icon class="mt-2">
                  <v-tooltip right>
                    <template v-slot:activator="{ on, attrs }">
                      <v-icon color="secondary" v-bind="attrs" v-on="on"
                        >mdi-account</v-icon
                      >
                    </template>
                    <span>User account</span>
                  </v-tooltip>
                </v-list-item-icon>
                <v-list-item-content>
                  <div class="text-caption">User</div>
                  <div class="text-body-1">
                    {{
                      $wob.client.user?.email ||
                      $wob.client.user?.username ||
                      'Anonymous'
                    }}
                  </div>
                </v-list-item-content>
              </v-list-item>

              <v-divider class="mb-4" inset></v-divider>

              <!-- Connection Section -->
              <v-list-item>
                <v-list-item-icon>
                  <v-tooltip right>
                    <template v-slot:activator="{ on, attrs }">
                      <v-icon color="secondary" v-bind="attrs" v-on="on"
                        >mdi-shield-key</v-icon
                      >
                    </template>
                    <span
                      >Azure Digital Twins connection (sign out to change)</span
                    >
                  </v-tooltip>
                </v-list-item-icon>
                <v-list-item-content>
                  <div class="text-caption">Tenant ID</div>
                  <div class="text-body-1">
                    {{ connectionProfile.tenantId }}
                  </div>
                  <div class="text-caption mt-2">Client ID</div>
                  <div class="text-body-1">
                    {{ connectionProfile.clientId }}
                  </div>
                  <div class="text-caption mt-2">Host</div>
                  <div class="text-body-1">
                    {{ connectionProfile.hostName }}
                  </div>
                </v-list-item-content>
              </v-list-item>

              <!-- Local Cache Section (only shown when authenticated) -->
              <template v-if="$wob.client.isAuthenticated">
                <v-divider class="mb-4 mt-2" inset></v-divider>

                <v-list-item>
                  <v-list-item-icon>
                    <v-tooltip right>
                      <template v-slot:activator="{ on, attrs }">
                        <v-icon color="secondary" v-bind="attrs" v-on="on"
                          >mdi-database</v-icon
                        >
                      </template>
                      <span>Local browser cache</span>
                    </v-tooltip>
                  </v-list-item-icon>
                  <v-list-item-content>
                    <!-- Cache count -->
                    <div class="text-caption">Cache</div>
                    <div v-if="$wob.isSynced" class="text-body-1">
                      {{ $wob.inCache }} individuals
                    </div>
                    <div v-else-if="$wob.inCache > 0" class="text-body-1">
                      {{ $wob.inCache }} individuals (syncing...)
                    </div>
                    <div v-else class="text-body-1 text--disabled">
                      Not synced
                    </div>
                    <!-- Last Synced timestamp -->
                    <div v-if="$wob.lastSyncTime" class="mt-2">
                      <div class="text-caption">Last Synced</div>
                      <div class="text-body-1">
                        <span class="text-caption">date</span>
                        {{ formatSyncDate($wob.lastSyncTime) }}&nbsp;
                        <span class="text-caption">time</span>
                        {{ formatSyncTime($wob.lastSyncTime) }}
                      </div>
                    </div>
                  </v-list-item-content>
                  <v-list-item-action class="align-self-end">
                    <v-tooltip left>
                      <template v-slot:activator="{ on, attrs }">
                        <v-btn
                          fab
                          x-small
                          color="secondary"
                          :disabled="!$wob.isSynced"
                          :loading="!$wob.isSynced"
                          @click="resync"
                          v-bind="attrs"
                          v-on="on"
                        >
                          <v-icon small>mdi-refresh</v-icon>
                        </v-btn>
                      </template>
                      <span>Re-sync from server</span>
                    </v-tooltip>
                  </v-list-item-action>
                </v-list-item>
              </template>

              <v-divider class="mb-4 mt-2" inset></v-divider>

              <!-- Version Section -->
              <v-list-item>
                <v-list-item-icon>
                  <v-tooltip right>
                    <template v-slot:activator="{ on, attrs }">
                      <v-icon color="secondary" v-bind="attrs" v-on="on"
                        >mdi-tag</v-icon
                      >
                    </template>
                    <span>Application version and build info</span>
                  </v-tooltip>
                </v-list-item-icon>
                <v-list-item-content>
                  <div class="text-caption">Version</div>
                  <div class="text-body-1">
                    {{ appVersion }}
                    <v-chip v-if="commitSha" x-small outlined class="ml-2">{{
                      commitSha
                    }}</v-chip>
                  </div>
                  <template v-if="buildTime">
                    <div class="text-caption mt-2">Build</div>
                    <div class="text-body-1">
                      <span class="text-caption">date</span>
                      {{ formatSyncDate(buildTime) }}&nbsp;
                      <span class="text-caption">time</span>
                      {{ formatSyncTime(buildTime) }}
                    </div>
                  </template>
                </v-list-item-content>
              </v-list-item>
            </v-list>
            <v-card-actions>
              <!-- show logout when authenticated -->
              <template v-if="$wob.client.isAuthenticated">
                <v-btn
                  text
                  color="secondary"
                  v-if="$wob.client.isAuthenticated"
                  @click="$wob.logout()"
                >
                  Sign out
                </v-btn>
              </template>
              <template v-else>
                <!-- show login when not authenticated -->
                <v-btn
                  text
                  color="secondary"
                  v-if="!$wob.client.isAuthenticated"
                  @click="signin"
                >
                  Sign in
                </v-btn>

                <!-- ADT settings dialogue -->
                <!-- TODO introduce revert on the three fields -->
                <v-dialog width="420px" v-model="profileSettingsVisible">
                  <template v-slot:activator="{ on, attrs }">
                    <!-- display: block -->
                    <v-btn text color="secondary" v-on="on" v-bind="attrs">
                      ADT Settings
                    </v-btn>
                  </template>
                  <v-card>
                    <!-- display: block -->
                    <v-toolbar color="lime darken-2" dark class="mb-4">
                      <v-toolbar-title
                        >Azure Digital Twin Settings</v-toolbar-title
                      >
                      <v-spacer></v-spacer>
                      <!-- display: block -->
                      <v-btn icon @click.stop="profileSettingsVisible = false">
                        <v-icon>mdi-close</v-icon>
                      </v-btn>
                    </v-toolbar>

                    <v-card-text>
                      <v-list flat>
                        <v-list-item>
                          <v-list-item-content>
                            <!-- display: block -->
                            <v-text-field
                              v-model="newTenantId"
                              dense
                              clearable
                              label="Microsoft Entra Directory ID (Tenant ID)"
                            ></v-text-field>
                          </v-list-item-content>
                        </v-list-item>

                        <v-list-item>
                          <v-list-item-content>
                            <v-text-field
                              v-model="newClientId"
                              dense
                              clearable
                              label="Directory App Registration ID (Client ID)"
                            ></v-text-field>
                          </v-list-item-content>
                        </v-list-item>

                        <v-list-item>
                          <v-list-item-content>
                            <v-text-field
                              v-model="newHostName"
                              dense
                              clearable
                              label="Digital Twins Host Name"
                            ></v-text-field>
                          </v-list-item-content>
                        </v-list-item>
                      </v-list>
                    </v-card-text>

                    <v-card-actions>
                      <!-- display: block -->
                      <v-btn
                        text
                        color="secondary"
                        @click.stop.prevent="closeAndUseSettings"
                      >
                        Use Settings
                      </v-btn>
                      <!-- display: block -->
                      <v-btn
                        text
                        color="secondary"
                        @click.stop.prevent="profileSettingsVisible = false"
                      >
                        Close and don't use
                      </v-btn>
                    </v-card-actions>
                  </v-card>
                </v-dialog>
              </template>
            </v-card-actions>
          </v-card>
        </v-menu>
      </template>
    </v-app-bar>

    <v-dialog
      v-model="showCreate"
      max-width="600"
      transition="dialog-top-transition"
    >
      <v-card style="height: 80vh; display: flex; flex-direction: column">
        <v-toolbar color="lime darken-2" dark>
          <v-toolbar-title>Create Individual</v-toolbar-title>
          <v-spacer></v-spacer>
          <!-- display: block -->
          <v-btn icon @click="showCreate = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-toolbar>
        <div class="flex-grow-1" style="height: calc(80vh - 64px)">
          <create-individual
            @create="onCreate"
            class="pa-4"
          ></create-individual>
        </div>
      </v-card>
    </v-dialog>

    <v-main app class="fill-height">
      <v-container fluid class="fill-height">
        <v-fade-transition mode="out-in">
          <router-view></router-view>

          <!-- 
            TODO the key forces both route update and create/mount rather than reuse of component on individual view
            can transitions be applied without this?
            <router-view :key="$route.path"></router-view> -->
        </v-fade-transition>
      </v-container>
    </v-main>

    <v-footer app class="justify-center text-body-1"
      ><v-icon small color="black">mdi-copyright </v-icon> 2019-{{
        thisYear
      }}
      Web of Buildings Scandinavia AB</v-footer
    >
  </v-app>
</template>

<script>
import CreateIndividual from './components/CreateIndividual.vue'
// import Vue from 'vue'

export default {
  components: { CreateIndividual },
  name: 'App',

  data: () => ({
    drawer: false,
    menu: [
      { icon: 'mdi-magnify', title: 'Search', to: '/search' },
      { icon: 'mdi-brain', title: 'Bot', to: '' },
      { icon: 'mdi-database-edit-outline', title: 'Manage', to: '' },
      { icon: 'mdi-file-tree', title: 'Trees', to: '/tree' },
      { icon: 'mdi-cube-outline', title: 'Assets', to: '/assets' },
      { icon: 'mdi-map-outline', title: 'Map', to: '/map' },
      { icon: 'mdi-login', title: 'Sign In', to: '/hierarchy' },
    ],
    manageMenu: [
      {
        icon: 'mdi-plus',
        title: 'Create Individual',
        disable: false,
      },
      { icon: 'mdi-graph-outline', title: 'Upload Graph', disable: true },
      {
        icon: 'mdi-file-document-multiple',
        title: 'Add Documents',
        disable: true,
      },
    ],
    accessToken: 'no read yet',
    connectionProfile: {
      kind: 'adt',
      tenantId: '',
      clientId: '',
      hostName: '',
    },
    newTenantId: '',
    newClientId: '',
    newHostName: '',
    profileVisible: false,
    profileSettingsVisible: false,
    showBot: false,
    showCreate: false,
  }),

  computed: {
    cacheFullSize() {
      return this.$wob.size
    },
    cacheCurrentSize() {
      return this.$wob.inCache
    },
    isSynced() {
      return this.$wob.isSynced
    },
    // Sync progress from the client (twins loaded/total).
    syncProgress() {
      return this.$wob.syncProgress
    },
    // True if loading is in progress (has progress but not yet synced).
    isLoading() {
      // The next engine says when it syncs, total or not; 0.9.x only when
      // it knows a total.
      if (this.$wob.isSyncing !== undefined) return this.$wob.isSyncing
      return this.syncProgress.total > 0 && !this.isSynced
    },
    // Progress percentage for the progress bar.
    syncProgressPercent() {
      if (!this.syncProgress.total || this.syncProgress.total === 0) return 0
      return Math.round(
        (this.syncProgress.loaded / this.syncProgress.total) * 100
      )
    },
    thisYear() {
      return new Date().getFullYear()
    },
    // Version and build info - injected by CI/CD at build time
    // Falls back to package.json version + '-dev' for local development
    appVersion() {
      if (process.env.VUE_APP_VERSION) {
        return process.env.VUE_APP_VERSION
      }
      // Local dev: use package.json version (injected via vue.config.js) with -dev suffix
      const pkgVersion = process.env.VUE_APP_PACKAGE_VERSION
      return pkgVersion ? `${pkgVersion}-dev` : '0.0.0-dev'
    },
    buildTime() {
      const timestamp = process.env.VUE_APP_BUILD_TIME
      if (!timestamp) return null
      return timestamp
    },
    commitSha() {
      const sha = process.env.VUE_APP_COMMIT_SHA
      return sha ? sha.substring(0, 7) : null
    },
  },
  mounted() {
    console.log('app.mounted')
    // console.log('app', Array.isArray(this.$wob.index.ids))

    const url = new URL(document.location)
    const params = new URLSearchParams(url.search)
    const focusParam = params.get('focus')
    const tenantId = params.get('tenantid')
    const clientId = params.get('clientid')
    const hostName = params.get('hostname')
    console.log('app.created.params', focusParam, tenantId, clientId, hostName)

    // Set connection profile from the received params, or read from cache.
    // The URL params are ADT-shaped (tenantid/clientid/hostname); future PCX
    // boots will skip this branch and rely on the persisted profile.
    if (tenantId && clientId && hostName) {
      this.connectionProfile.kind = 'adt'
      this.connectionProfile.tenantId = tenantId
      this.connectionProfile.clientId = clientId
      this.connectionProfile.hostName = hostName
      // TODO temporary solution to ensure that $wob is created...
      const self = this
      setTimeout(
        async () => await self.$wob.updateProfile(this.connectionProfile),
        2000
      )
    } else {
      this.connectionProfile = this.$wob.connectionProfile
    }
    console.log('app.mounted.connection-profile', this.connectionProfile)
    // As the activator slot is used for the dialogue the open dialogue method isnt called,
    // and hence the variables for the dialogue is initiated here.
    // TODO seem to be set sevarl palces... refactor.
    this.newTenantId = this.connectionProfile.tenantId
    this.newClientId = this.connectionProfile.clientId
    this.newHostName = this.connectionProfile.hostName

    // Parse focus param to an Iri.
    const focusWellformed = focusParam && this.$wob.owl.isIri(focusParam)
    console.log('app.created.focus.wellformed', focusWellformed)
    if (focusWellformed) {
      const focus = this.$wob.owl.iri(focusParam)
      console.log('app.created.focus.iri', focus)
      // TODO temporary solution to allow the read to finalize.
      // The individual view read must trigger a read of the individual.
      setTimeout(() => {
        this.$router.push(`/individual/${encodeURIComponent(focus.value)}`)
      }, 2000)
    }
  },
  created() {
    this.manageMenu[0].handler = this.onCreateIndividual
    this.manageMenu[1].handler = () => null
    this.manageMenu[2].handler = () => null
    console.log('APP CREATED', this.$constraints)
  },
  methods: {
    resync() {
      console.log('App: resync button clicked')
      this.$wob.resync()
    },
    formatSyncDate(timestamp) {
      if (!timestamp) return ''
      const date = new Date(timestamp)
      return date.toLocaleDateString()
    },
    formatSyncTime(timestamp) {
      if (!timestamp) return ''
      const date = new Date(timestamp)
      return date.toLocaleTimeString()
    },
    signin: function () {
      this.profileVisible = false
      this.$wob.login()
    },
    outside: function (e) {
      console.log('OUTSIDE', e)
    },
    openSettings: function () {
      this.profileSettingsVisible = true
      this.newTenantId = this.connectionProfile.tenantId
      this.newClientId = this.connectionProfile.clientId
      this.newHostName = this.connectionProfile.hostName
    },
    closeAndUseSettings: async function () {
      this.profileSettingsVisible = false
      console.log('app.methods.close-and-use-settings')

      // Updates the local (App) version of the setting.
      // TODO Vue observable didnt work on the profile hence keeping the local version manually updated...
      this.connectionProfile.tenantId = this.newTenantId
      this.connectionProfile.clientId = this.newClientId
      this.connectionProfile.hostName = this.newHostName

      await this.$wob.updateProfile({
        kind: 'adt',
        tenantId: this.newTenantId,
        clientId: this.newClientId,
        hostName: this.newHostName,
      })
    },
    // TODO consider embedding in HTML...
    onCreateIndividual: function () {
      console.log('methods.on-create-individual')
      this.showCreate = true
    },
    // Parameter id is an Iri.
    onCreate: function (id) {
      this.showCreate = false
      this.$router.push(`/individual/${encodeURIComponent(id.value)}`)
    },
  },
}
</script>

<style>
html,
body,
#app {
  height: 100%;
  margin: 0;
}

/* Sync status bar styles */
.sync-status-bar {
  display: flex;
  align-items: center;
}

@keyframes spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}
</style>
